import { useState, useEffect } from 'react';
import styles from './StartPage.module.css';
import LoginModal from '../../components/Modals/LoginModal/LoginModal';
import RegisterModal from '../../components/Modals/RegisterModal/RegisterModal';
import ForgotPasswordModal from '../../components/Modals/ForgotPasswordModal/ForgotPasswordModal';
import { SettingsService, AuthSettings } from '../../service/settings.service';
import { AuthService } from '../../service/auth.service';

type Mode = 'login' | 'register' | 'forgot' | null;

interface StartPageProps {
  onLogin: () => Promise<void>; 
}

export default function StartPage({ onLogin }: StartPageProps) {
  const [mode, setMode] = useState<Mode>('login');
  const [authSettings, setAuthSettings] = useState<AuthSettings>({
    signUpOpen: true,
    resetOpen: true,
    tgBot: "@wtm2_dev_bot"
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await SettingsService.getAuthSettings();
        setAuthSettings(settings);
      } catch (error) {
        console.error("Failed to fetch auth settings:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleSwitchMode = (newMode: Mode) => {
    if (newMode === 'register' && !authSettings.signUpOpen) {
      console.warn('Registration is disabled');
      return;
    }
    
    if (newMode === 'forgot' && !authSettings.resetOpen) {
      console.warn('Password reset is disabled');
      return;
    }
    
    setMode(newMode);
  };

  const handleLogin = async (data: { login: string; password: string }) => {
    const result = await AuthService.login(data);
    
    if (!result.requires2fa) {
      await onLogin();
    }
    
    return result;
  };

  const handle2faVerify = async (data: { login: string; password: string; code: string }) => {
    await AuthService.verify2fa(data);
    await onLogin();
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingSpinner}>
          <div className={styles.spinner}></div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <LoginModal
        isOpen={mode === 'login'}
        onClose={() => setMode(null)}
        onSwitchMode={handleSwitchMode}
        onLogin={handleLogin}
        on2faVerify={handle2faVerify}
        registrationEnabled={authSettings.signUpOpen}
        resetEnabled={authSettings.resetOpen}
        tgBot={authSettings.tgBot}
      />

      <RegisterModal
        isOpen={mode === 'register'}
        onClose={() => setMode(null)}
        onSwitchMode={handleSwitchMode}
        registrationEnabled={authSettings.signUpOpen}
        tgBot={authSettings.tgBot}
      />

      <ForgotPasswordModal
        isOpen={mode === 'forgot'}
        onClose={() => setMode(null)}
        onSwitchMode={handleSwitchMode}
        resetEnabled={authSettings.resetOpen}
      />
    </div>
  );
}